<!-- src/components/dashboard/tasks/TaskFormDialog.vue
  FT-076: диалог создания задачи. Все поля (название, приоритет, исполнитель, срок,
  повтор, напоминание) в одном месте. taskable можно предзадать (для карточек). -->
<template>
  <v-dialog :value="value" max-width="540" @input="$emit('input', $event)">
    <v-card data-testid="task-form-dialog">
      <v-card-title class="text-h6 d-flex align-center">
        <v-icon left>mdi-checkbox-marked-circle-plus-outline</v-icon>
        {{ $t('tasks.new_task') }}
      </v-card-title>
      <v-card-text>
        <v-form ref="form" @submit.prevent="save">
          <v-text-field v-model="title" :label="$t('tasks.title_placeholder')" autofocus
                        :rules="titleRules" outlined dense class="mb-1" />
          <v-row dense>
            <v-col cols="12" sm="6">
              <v-select v-model="priority" :items="priorityOptions" :label="$t('tasks.priority')"
                        outlined dense hide-details prepend-inner-icon="mdi-flag-outline" />
            </v-col>
            <v-col cols="12" sm="6">
              <v-select v-model="assignee" :items="assigneeOptions" :label="$t('tasks.assignee')"
                        outlined dense hide-details clearable prepend-inner-icon="mdi-account-outline" />
            </v-col>
          </v-row>
          <v-row dense class="mt-1">
            <v-col cols="12" sm="6">
              <component :is="pickerWrapper" v-model="dueMenu" v-bind="pickerWrapperProps">
                <template #activator="{ on, attrs }">
                  <v-text-field :value="dueDisplay" :label="$t('tasks.due')" readonly outlined dense hide-details
                                clearable prepend-inner-icon="mdi-calendar" v-bind="attrs" v-on="on"
                                @click:clear="due = null" />
                </template>
                <v-date-picker v-model="due" :locale="$i18n.locale" first-day-of-week="1" no-title scrollable
                               @input="dueMenu = false" />
              </component>
            </v-col>
            <v-col cols="12" sm="6">
              <v-select v-model="recurringInterval" :items="recurringOptions" :label="$t('tasks.recurring_interval')"
                        outlined dense hide-details prepend-inner-icon="mdi-repeat" />
            </v-col>
          </v-row>
          <component :is="pickerWrapper" v-model="remindMenu" v-bind="pickerWrapperProps">
            <template #activator="{ on, attrs }">
              <v-text-field :value="remindDisplay" :label="$t('tasks.remind')" readonly outlined dense hide-details
                            clearable class="mt-3" prepend-inner-icon="mdi-bell-outline" v-bind="attrs" v-on="on"
                            @click:clear="clearRemind" />
            </template>
            <v-card>
              <v-tabs v-model="remindTab" grow height="40">
                <v-tab>{{ $t('tasks.due') }}</v-tab>
                <v-tab :disabled="!remindDate">{{ remindTime || '--:--' }}</v-tab>
              </v-tabs>
              <v-date-picker v-if="remindTab === 0" v-model="remindDate" :locale="$i18n.locale" first-day-of-week="1"
                             no-title scrollable @input="remindTab = 1" />
              <v-time-picker v-else v-model="remindTime" format="24hr" scrollable @click:minute="remindMenu = false" />
            </v-card>
          </component>
        </v-form>
      </v-card-text>
      <v-card-actions class="px-6 pb-4">
        <v-spacer />
        <v-btn text :disabled="creating" @click="close">{{ $t('tasks.cancel') }}</v-btn>
        <v-btn color="primary" depressed :loading="creating" :disabled="!title.trim()" @click="save">
          {{ $t('tasks.add') }}
        </v-btn>
      </v-card-actions>
    </v-card>
    <v-snackbar v-model="snackbar" color="error" timeout="4000">{{ snackText }}</v-snackbar>
  </v-dialog>
</template>

<script>
import { mapState, mapGetters, mapActions } from "vuex";
import { parseCrmError } from "@/store/modules/tasks/api";
import mobilePickerMixin from "@/mixins/mobilePickerMixin";

export default {
  name: "TaskFormDialog",
  mixins: [mobilePickerMixin],
  props: {
    value: { type: Boolean, default: false },
    taskableType: { type: String, default: null },
    taskableId: { type: [String, Number], default: null },
  },
  data: () => ({
    title: "", priority: "normal", assignee: null, due: null, recurringInterval: null,
    dueMenu: false, remindMenu: false, remindTab: 0, remindDate: null, remindTime: null,
    snackbar: false, snackText: "",
  }),
  computed: {
    ...mapState("tasks", { creating: (s) => s.creating, users: (s) => s.users }),
    ...mapGetters(["currentUserId"]),
    priorityOptions() { return ["low", "normal", "high"].map((p) => ({ value: p, text: this.$t("tasks.priority_" + p) })); },
    recurringOptions() {
      return [{ value: null, text: this.$t("tasks.recurring_interval_none") }]
        .concat(["daily", "weekly", "monthly"].map((i) => ({ value: i, text: this.$t("tasks.recurring_interval_" + i) })));
    },
    assigneeOptions() { return this.users.map((u) => ({ value: u.id, text: u.name })); },
    titleRules() { return [(v) => !!(v && v.trim()) || this.$t("tasks.title_required")]; },
    dueDisplay() { return this.due ? new Date(`${this.due}T00:00:00`).toLocaleDateString(this.$i18n.locale) : ""; },
    remindDisplay() {
      if (!this.remindDate) return "";
      return new Date(`${this.remindDate}T${this.remindTime || "09:00"}:00`).toLocaleString(this.$i18n.locale, { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
    },
  },
  watch: {
    value(open) { if (open) { this.fetchTaskUsers().catch(() => {}); if (this.assignee == null) this.assignee = this.currentUserId; } },
  },
  methods: {
    ...mapActions("tasks", ["createTask", "fetchTaskUsers"]),
    clearRemind() { this.remindDate = null; this.remindTime = null; this.remindTab = 0; },
    reset() {
      this.title = ""; this.priority = "normal"; this.assignee = this.currentUserId; this.due = null;
      this.recurringInterval = null; this.clearRemind();
      if (this.$refs.form) this.$refs.form.resetValidation();
    },
    close() { this.$emit("input", false); },
    async save() {
      if (!this.title.trim() || this.creating) return;
      const payload = {
        title: this.title.trim(), priority: this.priority,
        recurring_interval: this.recurringInterval || null,
        due_at: this.due ? `${this.due}T23:59:00` : null,
        remind_at: this.remindDate ? `${this.remindDate}T${this.remindTime || "09:00"}:00` : null,
        assigned_to_id: this.assignee || undefined,
      };
      if (this.taskableType) { payload.taskable_type = this.taskableType; payload.taskable_id = this.taskableId; }
      try {
        const task = await this.createTask(payload);
        this.$emit("saved", task);
        this.reset();
        this.close();
      } catch (e) { this.snackText = parseCrmError(e).fallback; this.snackbar = true; }
    },
  },
};
</script>
